<script setup>
import { ref, watch } from "vue"
import { useI18n } from "@/app/composables/useI18n"
import { toast } from "@/app/utils/notification"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import PersonalInfoForm from "@/user-account/components/auth/PersonalInfoForm.vue"
import { useUserService } from "@/user-account/composables/useUserService.ts"
import { useValidation } from "@/user-account/composables/useValidation"
import { resetErrors, validate } from "@/user-account/utils/input-rules/validation"
import { getProfile, updateProfile } from "@/user-account/api/index"

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(["close", "success"])

useUserService()
const { validateInputs } = useValidation()
const { t } = useI18n()

const isLoading = ref(false)
const personalData = ref({
  firstName: null,
  lastName: null,
  dateOfBirth: null,
  city: null,
  addressLine1: null,
  addressLine2: null,
  postalCode: null,
  countryId: null,
})

async function loadData() {
  try {
    const { data } = await getProfile()
    personalData.value = data?.personalData || {}
  } catch (error) {
    console.error("Failed to load profile data:", error)
    toast.error(t("kyc.failed_to_load_profile") || "Failed to load profile data")
  }
}

function closeModal() {
  emit("close")
}

async function onPersonalInfoSubmit(form) {
  validateInputs(form, t)
  const hasErrors = form.some(input => input.error)
  if (!hasErrors) {
    if (!validate(form)) {
      return
    }
  }
  
  try {
    isLoading.value = true
    
    const formData = form.reduce((acc, input) => {
      if (input.name) {
        acc[input.name] = input.value
      }
      return acc
    }, {})

    const { data: updateData } = await updateProfile({
      personalData: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        city: formData.city,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        postalCode: formData.postalCode,
        countryId: formData.countryId ? formData.countryId : "",
      },
    })

    resetErrors(form)
    toast.success(t("kyc.personal_info_submitted") || "Personal information updated successfully")
    emit("success")
    closeModal()
  } catch (error) {
    console.error("Failed to update personal info:", error)
    toast.error(t("kyc.submission_error") || "Failed to update personal information")
  } finally {
    isLoading.value = false
  }
}

// Load data when modal opens
watch(() => props.show, (newShow) => {
  if (newShow) {
    loadData()
  }
})
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-mask">
      <div class="modal-container">
        <div class="modal-detail">
          <div class="close-btn" @click="closeModal">
            <div>{{ t("general.close") }}</div>
            <img src="/assets/images/close-modal-icon.svg" alt="">
          </div>

          <div class="modal-body">
            <div class="modal-header">
              <h3>{{ t("kyc.personal_information") || "Personal Information" }}</h3>
              <p class="modal-subtitle">{{ t("kyc.personal_info_description") || "Please provide your personal information to complete KYC Level 1" }}</p>
            </div>

            <div class="form-container">
              <PersonalInfoForm
                :personal-data="personalData"
                @submit="onPersonalInfoSubmit"
              />
              
              <div class="modal-actions">
                <BaseButton
                  type="button"
                  class="cancel-btn secondary wide"
                  :disabled="isLoading"
                  @click="closeModal"
                >
                  {{ t("common.cancel") }}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-container {
  width: 491px;
  margin: auto;
  padding: 39px 20px 20px 20px;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: auto;
  position: relative;
  width: 100%;
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 24px;
}

.modal-header {
  margin-bottom: 24px;
  text-align: center;

  h3 {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--be-primary-text-color);
  }

  .modal-subtitle {
    color: var(--be-secondary-text-color);
    font-size: 14px;
    margin: 0;
  }
}

.form-container {
  // Custom styling for PersonalInfoForm within modal
  :deep(.form-fields) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
    
    // First row: firstName and lastName side by side
    > *:nth-child(1),
    > *:nth-child(2) {
      // These automatically occupy the first row, side by side
    }
    
    // All other fields span full width (except city and postal code)
    > *:nth-child(3),
    > *:nth-child(4),
    > *:nth-child(5),
    > *:nth-child(6) {
      grid-column: 1 / -1;
    }
    
    // City and postal code on same line (7th and 8th fields)
    > *:nth-child(7),
    > *:nth-child(8) {
      // These automatically occupy their row, side by side
    }
  }

  .modal-actions {
    display: flex;
    justify-content: center;
    margin-top: 16px;

    .cancel-btn.secondary {
      background: var(--be-secondary-btn);
      color: var(--be-secondary-btn-label);
      border: 1px solid var(--be-border-color);

      &:hover:not(:disabled) {
        background: var(--be-secondary-btn-hover);
      }
    }
  }
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

@media screen and (max-width: 768px) {
  .modal-container {
    width: 95%;
    padding: 10px;
  }
  
  .form-container {
    :deep(.form-fields) {
      // Stack all fields vertically on mobile - single column grid
      grid-template-columns: 1fr;
      
      // Reset all fields to span single column
      > *:nth-child(1),
      > *:nth-child(2),
      > *:nth-child(3),
      > *:nth-child(4),
      > *:nth-child(5),
      > *:nth-child(6),
      > *:nth-child(7),
      > *:nth-child(8) {
        grid-column: 1;
      }
    }
  }
}
</style> 